import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, CardBody, Input } from 'reactstrap';
import Select from 'react-select';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import SweetAlert from 'react-bootstrap-sweetalert';
import { getQuoteBuilderURL } from '../../../helpers/common';
import { useHistory } from 'react-router';

const QUOTE = gql`
  query getQuote($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      CustomerId
      Description
      ProposalTitle
      ValidUntil
      CustomerLogo
      WorkflowStep
      AcceptedQuote
      Status
    }
  }
`;

const CUSTOMERS = gql`
  query getCustomers {
    Customers {
      ContactID
      Name
      FirstName
      LastName
      EmailAddress
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const QuoteBuilderBillingContactCAF = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  let history = useHistory();

  const quoteIDParam = history.location.pathname.split('/').pop();

  // initialize states
  const [stepError, setStepError] = useState(null);
  const [disableQuote, setDisableQuote] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [customerError, setCustomerError] = useState(false);
  const [customerId, setCustomerId] = useState(null);

  // initialize apollo graphql queries and mutations
  const [autoSaveQuote] = useMutation(UPDATE_QUOTE);
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const { data: existingQuoteData } = useQuery(QUOTE, {
    variables: { QuoteId: props.quoteId },
  });
  const { data: customersData, refetch: refetchCustomers } = useQuery(CUSTOMERS);

  const customerOptions = useMemo(() => {
    if (!customers) return [];
    return customers
      .filter((c) => c.ContactID !== 'error')
      .map((c) => {
        let label = c.Name;
        if (c.FirstName && c.LastName) label += ` (${c.FirstName} ${c.LastName})`;
        return { value: c.ContactID, label };
      });
  }, [customers]);

  useEffect(() => {
    if (!existingQuoteData?.Quote) return;
    const { Quote } = existingQuoteData;

    if (!['BILLING_CONTACT_CAF', 'CUSTOMER_ACCEPTED'].includes(Quote.WorkflowStep)) {
      history.replace(getQuoteBuilderURL(Quote.WorkflowStep, Quote.QuoteId));
      return;
    }

    setDisableQuote(Quote.Status === 'accepted');
    setCustomerId(Quote.CustomerId ?? null);
  }, [existingQuoteData, history]);

  useEffect(() => {
    if (!customersData?.Customers) return;
    if (customersData.Customers.some((c) => c.ContactID === 'error')) setCustomerError(true);
    setCustomers(customersData.Customers);
  }, [customersData]);

  useEffect(() => {
    if (!customerId || !customerOptions.length) return;
    const match = customerOptions.find((o) => o.value === customerId);
    if (match) setSelectedCustomer(match);
  }, [customerOptions, customerId]);

  const proceedNextStep = (e) => {
    //field validation
    if (!selectedCustomer) {
      setStepError({
        error: true,
        message: 'Please select a Billing Account',
      });
    } else {
      updateQuote({
        variables: {
          QuoteInput: {
            QuoteId: quoteIDParam,
            WorkflowStep: 'CUSTOMISE_CAF',
          },
        },
      }).then(() => {
        history.replace(`/customise-caf/${existingQuoteData.Quote.QuoteId}`);
      });
    }
  };

  const changeCustomer = (e) => {
    setCustomerId(e ? e.value : null);
    setSelectedCustomer(e);
    autoSaveQuote({
      variables: {
        QuoteInput: {
          QuoteId: quoteIDParam,
          CustomerId: e ? e.value + "" : null,
        },
      },
      refetchQueries: ['getCurrentQuote'],
    });
  };

  const refreshCustomers = () => {
    setCustomers(null);
    setSelectedCustomer(null);
    refetchCustomers().then(({ data }) => {
      if (data?.Customers) {
        setCustomers(data.Customers);
      }
    });
  };

  return (
    <React.Fragment>
      {disableQuote && <div className="overlay"></div>}

      {stepError && stepError.error === true && (
        <SweetAlert
          error
          title="Error"
          onConfirm={(e) => setStepError({ error: false, message: '' })}
        >
          {stepError.message}
        </SweetAlert>
      )}

      {existingQuoteData && existingQuoteData.Quote && (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">{existingQuoteData.Quote.Description} - Billing Account</h4>
                </Col>
              </Row>
            </div>

            <Card>
              <CardBody>
                <h4 className="mt-0 header-title">Billing Account</h4>
                <table className="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">
                        Billing Account{' '}
                        <span className="text-danger">*</span>
                      </th>
                      <td>
                        {customerError ? (
                          <span className="text-danger">
                            Failed to load billing accounts
                          </span>
                        ) : (
                          <>
                            <Select
                              isClearable
                              isDisabled={customers === null}
                              options={customerOptions}
                              value={selectedCustomer}
                              placeholder={customers === null ? 'Loading...' : 'Please Select...'}
                              onChange={changeCustomer}
                            />
                            <small className="text-muted">
                              Click <a href="https://jolane.switchcloud.au/customers/new" target="_blank" rel="noreferrer" className="btn-link">here</a>
                              {' to add a new customer and then '}
                              <button type="button" className="btn-link" onClick={refreshCustomers}>here</button> to refresh this list
                            </small>
                          </>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <th scope="row">Account ID </th>
                      <td>
                        <Input
                          type="text"
                          value={customerId ?? ''}
                          readOnly
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardBody>
            </Card>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <button
                      className="btn-icon btn btn-primary btn-lg float-right"
                      onClick={proceedNextStep}
                    >
                      {' '}
                      <span className="btn-icon-label">
                        <i className="ion ion-md-arrow-forward mr-2"></i>
                      </span>{' '}
                      Next Step
                    </button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(
  QuoteBuilderBillingContactCAF
);
